import React, { useEffect, useState } from 'react';
import { Database, Terminal, Plus } from 'lucide-react';
import { cn } from '../../../lib/utils';
import DatasetSidebar from './DatasetSidebar';
import FunctionSidebar from './FunctionSidebar';
import { useAuth } from '../../../context/AuthContext';
import { FEATURE_INTEGRATION_UDF } from '../../../constants/capabilities';

const DataPanelTabs = ({
  sources,
  udfs,
  previewDataset,
  previewFunction,
  setPreviewDataset,
  setPreviewFunction,
  onConnect,
  onRemoveSource,
  onRebuildSource,
  onManageFunctions,
  onRemoveFunction,
  removingSourceId = null
}) => {
  const { capabilities } = useAuth();
  const udfEnabled = capabilities?.features?.[FEATURE_INTEGRATION_UDF] !== false;
  const [activeTab, setActiveTab] = useState('datasets');
  useEffect(() => {
    if (!udfEnabled && activeTab === 'functions') {
      setActiveTab('datasets');
    }
  }, [udfEnabled, activeTab]);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-950">
      {/* Tab Header */}
      <div className="flex items-center justify-between gap-1 px-2 pt-2 pb-1 shrink-0">
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => setActiveTab('datasets')}
            className={cn(
              "flex items-center gap-1 px-2 py-1 text-[9px] font-semibold uppercase tracking-wider transition-all rounded-md",
              activeTab === 'datasets'
                ? "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20"
                : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            )}
          >
            <Database size={11} />
            Datasets
          </button>
          <button
            onClick={() => {
              if (udfEnabled) setActiveTab('functions');
            }}
            disabled={!udfEnabled}
            className={cn(
              "flex items-center gap-1 px-2 py-1 text-[9px] font-semibold uppercase tracking-wider transition-all rounded-md",
              activeTab === 'functions'
                ? "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20"
                : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300",
              !udfEnabled && "opacity-60 cursor-not-allowed hover:text-gray-500 dark:hover:text-gray-400"
            )}
          >
            <Terminal size={11} />
            Functions
            {!udfEnabled && (
              <sup className="ml-0.5 text-[7px] font-semibold normal-case text-amber-700/90 dark:text-amber-300/90 align-super">
                pro
              </sup>
            )}
          </button>
        </div>
        <button
          onClick={activeTab === 'datasets' ? onConnect : (udfEnabled ? onManageFunctions : undefined)}
          disabled={activeTab === 'functions' && !udfEnabled}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-gray-200 text-gray-400 transition-all hover:border-emerald-300 hover:text-emerald-600 hover:bg-gray-50 dark:border-gray-700 dark:hover:border-emerald-700 dark:hover:text-emerald-400 dark:hover:bg-gray-900/50"
          title={activeTab === 'datasets' ? 'Add dataset' : 'Add function'}
          aria-label={activeTab === 'datasets' ? 'Add dataset' : 'Add function'}
        >
          <Plus size={12} />
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'datasets' && (
          <DatasetSidebar
            sources={sources}
            previewDataset={previewDataset}
            setPreviewDataset={(ds) => {
              setPreviewDataset(ds);
              if (ds) setPreviewFunction(null);
            }}
            onConnect={onConnect}
            onRemoveSource={onRemoveSource}
            onRebuildSource={onRebuildSource}
            removingSourceId={removingSourceId}
          />
        )}
        {activeTab === 'functions' && (
          <FunctionSidebar
            udfs={udfs}
            previewFunction={previewFunction}
            setPreviewFunction={(fn) => {
              setPreviewFunction(fn);
              if (fn) setPreviewDataset(null);
            }}
            onManageFunctions={onManageFunctions}
            onRemoveFunction={onRemoveFunction}
          />
        )}
      </div>
    </div>
  );
};

export default DataPanelTabs;
